import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';

@Component({
	standalone: true,
	selector: 'scheduled-call-reminder-dialog',
	templateUrl: './scheduled-call-reminder-dialog.component.html',
	imports: [MatDialogModule, MatButtonModule, MatMenuModule, MatIconModule],
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./scheduled-call-reminder-dialog.component.scss'],
})
export class ScheduledCallReminderDialogComponent implements OnInit, OnDestroy {
	readonly dialogRef = inject(
		MatDialogRef<ScheduledCallReminderDialogComponent>
	);
	readonly data = inject<ScheduledCallReminderDialogData>(MAT_DIALOG_DATA);
	currentTitle = '';
	titleColorClass = '';
	private updateInterval: any;

	get timeStatus(): { minutes: number; isOverdue: boolean } {
		const now = new Date();
		const scheduledTime = new Date(this.data.dateTime);
		const diffInMs = scheduledTime.getTime() - now.getTime();
		const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

		return {
			minutes: Math.abs(diffInMinutes),
			isOverdue: diffInMinutes < 0,
		};
	}

	get dialogTitle(): string {
		return this.currentTitle;
	}

	ngOnInit(): void {
		this.updateTitle();
		this.updateInterval = setInterval(() => {
			this.updateTitle();
		}, 60000); // Update every minute
	}

	ngOnDestroy(): void {
		if (this.updateInterval) {
			clearInterval(this.updateInterval);
		}
	}

	formatPhoneNumber(phoneNumber: string): string {
		if (!phoneNumber) return phoneNumber;

		// Remove all non-digit characters
		const digits = phoneNumber.replace(/\D/g, '');

		// Format as (xxx) xxx-xxxx if we have 10 digits
		if (digits.length === 10) {
			return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
		}

		// Return original if not 10 digits
		return phoneNumber;
	}

	onCompleted(): void {
		this.dialogRef.close({
			action: 'completed',
			applicationId: this.data.applicationId,
			applicationCallId: this.data.applicationCallId,
			application: this.data.application,
			applicationCall: this.data.applicationCall,
		});
	}

	onReschedule(): void {
		this.dialogRef.close({
			action: 'reschedule',
			applicationId: this.data.applicationId,
			applicationCallId: this.data.applicationCallId,
			application: this.data.application,
			applicationCall: this.data.applicationCall,
		});
	}

	onCancel(): void {
		this.dialogRef.close({
			action: 'cancel',
			applicationId: this.data.applicationId,
			applicationCallId: this.data.applicationCallId,
			application: this.data.application,
			applicationCall: this.data.applicationCall,
		});
	}

	onClose(): void {
		this.dialogRef.close({ action: 'close' });
	}

	onOpenApplication(): void {
		const url = `/applications/summary/${this.data.applicationId}`;
		window.open(url, '_blank');
	}

	private updateTitle(): void {
		const { minutes, isOverdue } = this.timeStatus;

		if (minutes === 0) {
			this.currentTitle = "It's time to call";
			this.titleColorClass = 'dialog-title-ontime';
		} else if (isOverdue) {
			this.currentTitle = `${minutes} minute${
				minutes !== 1 ? 's' : ''
			} late for the call`;
			this.titleColorClass = 'dialog-title-overdue';
		} else {
			this.currentTitle = `${minutes} minute${
				minutes !== 1 ? 's' : ''
			} until the call`;
			this.titleColorClass = 'dialog-title-ontime';
		}
	}
}

export interface ScheduledCallReminderDialogData {
	dateTime: Date;
  customerName: string;
  bestContactNumber: string;
  otherContactNumber: string;
  email: string;
  applicationId: string;
  applicationCallId: string;
  application: any;
  applicationCall: ApplicationCall;
}